import { useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid-premium';
import { useTableSettings } from 'src/core/tables/useTableSettings';
import type { tableSettingsProps } from 'src/core/tables/useTableSettings';
import tableHelper from 'src/core/tables/tableHelper';
import { useGetFullfillmentOrderRows, useGetFullfillmentOrders } from 'src/pages/warehouses/warehouseRequests';

export const useFullfillmentOrdersTable = (refreshKey = 0): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows, status } = useGetFullfillmentOrders(true, refreshKey);
  const columns: GridColDef[] = useMemo(() => [
    { field: 'Id', headerName: 'Id' },
    { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, renderCell: tableHelper.renderStatusCell },
    { field: 'CustomerName', headerName: 'Customer Name', width: 220 },
    { field: 'FulfillmentStrategy_Value', headerName: 'Fulfillment Strategy', valueGetter: tableHelper.getFieldKeyOrValue, width: 280 },
    { field: 'Priority_Value', headerName: 'Priority', valueGetter: tableHelper.getFieldKeyOrValue },
    { field: 'SelectedWarehouse', headerName: 'Selected Warehouse', width: 200 },
    { field: 'DateCreated', headerName: 'Date Created', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
    { field: 'DateFulfilled', headerName: 'Date Fulfilled', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
    { field: 'DeliveryAddress', headerName: 'Delivery Address' },
    { field: 'DispatchCommands', headerName: 'Dispatch Commands' },
    { field: 'ExternalCustomerId', headerName: 'External Customer Id' },
    { field: 'ExternalCustomerOrderRef', headerName: 'External Customer Order Ref' },
    { field: 'HasDispatchCommandsInStatusCreated', headerName: 'Has Dispatch Commands In Status Created', type: 'boolean' },
    { field: 'RequiredWarehouse', headerName: 'Required Warehouse' },
    { field: 'RequiresSingleDelivery', headerName: 'Requires Single Delivery', type: 'boolean' },
  ], []);
  const tableSettings = useTableSettings({ tableName: 'FullfillmentOrdersTable', idPath: ['Id'], defaultColumnVisibilityModel: { ExternalCustomerId: false, HasDispatchCommandsInStatusCreated: false }, columns, rows, status });
  tableSettings.loading = status === 'idle' || status === 'loading' || status === 'retry';
  return [columns, tableSettings];
};

export const useFullfillmentOrderRowsTable = (id: string, refreshKey = 0): [columns: GridColDef[], tableSettings: tableSettingsProps, createdDispatchCommandId?: number] => {
  const { rows: detail, loading, status } = useGetFullfillmentOrderRows(id, refreshKey);
  const createdDispatchCommand = useMemo(() => detail?.DispatchCommands?.find(command => command.Status?.Value?.toLowerCase() === 'created'), [detail]);
  const createdDispatchCommandId = !loading && status === 'success' ? createdDispatchCommand?.Id : undefined;
  const rows = useMemo(() => {
    const dispatchRows = createdDispatchCommand?.DispatchCommandRows || [];
    return detail?.FulfillmentOrderRows?.map(row => {
      const isReady = dispatchRows.some(dispatchRow => dispatchRow.ArticleNumber === row.ProductId);
      let statusText = '';
      if (isReady) statusText = 'Ready For Dispatch';
      else if (row.QtyOrdered != null && row.QtyFulfilled === row.QtyOrdered) statusText = 'Completed';
      else if (row.QtyOrdered != null && row.QtyDispatching === row.QtyOrdered) statusText = 'Dispatching';
      else if (row.QtyRequested > 0) statusText = 'Requested';
      return { ...row, Status: statusText };
    }) ?? [];
  }, [detail, createdDispatchCommand]);
  const columns: GridColDef[] = useMemo(() => [
    { field: 'RowNr', headerName: 'Row', width: 50 },
    { field: 'Status', headerName: 'Status', width: 140, renderCell: tableHelper.renderStatusCell },
    { field: 'ProductId', headerName: 'Product Id', width: 100 },
    { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id' },
    { field: 'Product', headerName: 'Product', width: 250 },
    { field: 'QtyToFulfill', headerName: 'Qty To Fulfill', type: 'number' },
    { field: 'QtyReserved', headerName: 'Qty Reserved', type: 'number' },
    { field: 'QtyRequested', headerName: 'Qty Requested', type: 'number' },
    { field: 'QtyDispatching', headerName: 'Qty Dispatching', type: 'number' },
    { field: 'QtyFulfilled', headerName: 'Qty Fulfilled', type: 'number' },
    { field: 'QtyOrdered', headerName: 'Qty Ordered', type: 'number' },
  ], []);
  const tableSettings = useTableSettings({ tableName: 'FullfillmentOrderRowsTable', idPath: ['RowNr'], hasToolbar: false, autoHeight: true, hideFooter: true, columns, rows, status });
  tableSettings.loading = loading;
  return [columns, tableSettings, createdDispatchCommandId];
};
